import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Trash2, Building2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SERVICES, COMPANY_TYPES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyClient = { business_name: "", contact_name: "", email: "", phone: "", company_type: "", services: [], notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [selected, setSelected] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.clients().then(setClients);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => clients.filter((c) => !q || `${c.business_name} ${c.contact_name}`.toLowerCase().includes(q.toLowerCase())),
    [clients, q]
  );

  const progress = (c) => {
    if (!c.checklist?.length) return 0;
    return Math.round((c.checklist.filter((i) => i.done).length / c.checklist.length) * 100);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const createClient = async () => {
    if (!form.business_name.trim() || !form.contact_name.trim()) return;
    await api.createClient(form);
    toast.success("Client added");
    setAddOpen(false); setForm(emptyClient); load();
  };

  const toggleCheck = async (idx) => {
    const updated = { ...selected, checklist: selected.checklist.map((it, i) => (i === idx ? { ...it, done: !it.done } : it)) };
    setSelected(updated);
    const saved = await api.updateClient(selected.id, { checklist: updated.checklist });
    setClients((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
  };

  const saveDetails = async (patch) => {
    const saved = await api.updateClient(selected.id, patch);
    setSelected(saved);
    setClients((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
    toast.success("Saved");
  };

  const confirmDelete = async () => {
    await api.deleteClient(delTarget.id);
    toast.success("Client removed");
    setDelTarget(null); setSelected(null); load();
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">Client records with onboarding checklists and notes.</p>
        </div>
        <Button onClick={() => { setForm(emptyClient); setAddOpen(true); }} data-testid="new-client-button" style={{ backgroundColor: "hsl(var(--brand))" }} className="rounded-full text-white">
          <Plus className="mr-1 h-4 w-4" /> New client
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="pl-9" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => {
          const p = progress(c);
          return (
            <button key={c.id} onClick={() => setSelected(c)} data-testid={`client-card-${c.id}`} className="animate-fade-up rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-900 text-white"><Building2 className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <p className="truncate font-display font-bold text-slate-900">{c.business_name}</p>
                  <p className="truncate text-xs text-slate-500">{c.contact_name}{c.company_type ? ` · ${c.company_type}` : ""}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1">
                {(c.services || []).slice(0, 3).map((s) => <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{s}</span>)}
              </div>
              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-slate-600">Onboarding</span>
                  <span className="font-mono-fin text-slate-500">{p}%</span>
                </div>
                <Progress value={p} className="h-2" />
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && <p className="col-span-full rounded-2xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500" data-testid="clients-empty">No clients yet. Add one or convert an enquiry.</p>}
      </div>

      {/* Add client dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">New Client</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5"><Label>Business name *</Label><Input data-testid="client-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Contact name *</Label><Input data-testid="client-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
            <div className="grid gap-1.5">
              <Label>Company type</Label>
              <Select value={form.company_type || undefined} onValueChange={(v) => set("company_type", v)}>
                <SelectTrigger data-testid="client-company-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Services</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SERVICES.map((s) => (
                  <label key={s} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs hover:bg-slate-50">
                    <Checkbox checked={form.services.includes(s)} onCheckedChange={() => toggleService(s)} /><span>{s}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createClient} data-testid="client-save" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">Add client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="client-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl">{selected.business_name}</SheetTitle>
                <p className="text-sm text-slate-500">{selected.contact_name}{selected.company_type ? ` · ${selected.company_type}` : ""}</p>
              </SheetHeader>
              <Tabs defaultValue="checklist" className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="checklist" data-testid="tab-checklist">Onboarding</TabsTrigger>
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                  <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
                </TabsList>

                <TabsContent value="checklist" className="mt-4 space-y-3">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <div className="mb-2 flex items-center justify-between text-sm"><span className="font-semibold text-slate-700">Onboarding progress</span><span className="font-mono-fin">{progress(selected)}%</span></div>
                    <Progress value={progress(selected)} className="h-2" />
                  </div>
                  {selected.checklist?.map((it, idx) => (
                    <label key={idx} data-testid={`checklist-item-${idx}`} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3 transition-colors hover:bg-slate-50">
                      <Checkbox checked={it.done} onCheckedChange={() => toggleCheck(idx)} data-testid={`checklist-checkbox-${idx}`} />
                      <span className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.label}</span>
                      {it.done && <Check className="ml-auto h-4 w-4 text-emerald-500" />}
                    </label>
                  ))}
                </TabsContent>

                <TabsContent value="details" className="mt-4 space-y-4">
                  <div className="grid gap-1.5"><Label>Email</Label><Input defaultValue={selected.email} onBlur={(e) => e.target.value !== selected.email && saveDetails({ email: e.target.value })} data-testid="client-detail-email" /></div>
                  <div className="grid gap-1.5"><Label>Phone</Label><Input defaultValue={selected.phone} onBlur={(e) => e.target.value !== selected.phone && saveDetails({ phone: e.target.value })} /></div>
                  <div className="grid gap-1.5">
                    <Label>Services</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {SERVICES.map((s) => {
                        const active = (selected.services || []).includes(s);
                        return (
                          <button key={s} onClick={() => saveDetails({ services: active ? selected.services.filter((x) => x !== s) : [...(selected.services || []), s] })}
                            className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-4">
                  <Label>Notes</Label>
                  <Textarea defaultValue={selected.notes} rows={10} data-testid="client-notes" onBlur={(e) => e.target.value !== selected.notes && saveDetails({ notes: e.target.value })} className="mt-1.5" placeholder="Add notes about this client…" />
                  <p className="mt-1 text-xs text-slate-400">Notes save automatically when you click away.</p>
                </TabsContent>
              </Tabs>

              <div className="mt-8 border-t border-slate-100 pt-4">
                <Button variant="outline" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setDelTarget(selected)} data-testid="delete-client-button">
                  <Trash2 className="mr-1 h-4 w-4" /> Remove client
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this client?</AlertDialogTitle>
            <AlertDialogDescription>{delTarget?.business_name} will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-client" className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
